
/**
 * MobileHamburgerMenu.tsx
 *
 * MobileHamburgerMenu component for the Marketplace UI.
 * Renders a mobile-friendly hamburger menu drawer with navigation and authentication actions.
 *
 * Features:
 * - Drawer menu for mobile navigation
 * - Links to Home, About, Contact, FAQ, Success Stories
 * - Sign in and logout actions
 * - Accessible and responsive UI
 *
 * @module MobileHamburgerMenu
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Props for the MobileHamburgerMenu component.
 * @property open - Whether the menu is open
 * @property onClose - Callback to close the menu
 * @property loggedIn - Whether the user is logged in
 * @property onSignIn - Callback to trigger sign in
 * @property onLogout - Callback to trigger logout
 */
interface MobileHamburgerMenuProps {
  open: boolean;
  onClose: () => void;
  loggedIn: boolean;
  onSignIn?: () => void;
  onLogout: () => void;
}


/**
 * MobileHamburgerMenu component
 * Renders a mobile-friendly hamburger menu drawer with navigation and authentication actions.
 *
 * @param {MobileHamburgerMenuProps} props - Component props
 * @returns {JSX.Element | null}
 */
const MobileHamburgerMenu: React.FC<MobileHamburgerMenuProps> = ({ open, onClose, loggedIn, onSignIn, onLogout }) => {
  const navigate = useNavigate();
  // Hide menu if not open
  if (!open) return null;
  // --- Main render: mobile menu drawer ---
  return (
    <div
      className="mobile-menu-drawer"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '80vw',
        maxWidth: 320,
        height: '100vh',
        background: '#fff',
        zIndex: 3000,
        boxShadow: '2px 0 16px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        padding: '32px 16px 16px 16px',
        gap: 16,
      }}
    >
      {/* Close button (top left) */}
      <button
        aria-label="Close menu"
        style={{ alignSelf: 'flex-start', background: 'none', border: 'none', fontSize: 28, color: '#7100EB', cursor: 'pointer' }}
        onClick={onClose}
      >
        &times;
      </button>
      {/* Home link */}
      <a
        href="/"
        style={{ color: '#7100EB', fontWeight: 700, fontSize: 18, marginBottom: 8, textDecoration: 'none', fontFamily: 'Poppins, Futura, "Century Gothic"' }}
        onClick={onClose}
      >
        Home
      </a>
      {/* About Us link (external) */}
      <a
        href="https://www.ellucian.com/about-us"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#151618', fontSize: 16, textDecoration: 'none', marginBottom: 8, fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto' }}
      >
        About Us
      </a>
      {/* Contact Us link (external) */}
      <a
        href="https://www.ellucian.com/contact-us"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#151618', fontSize: 16, textDecoration: 'none', marginBottom: 8, fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto' }}
      >
        Contact Us
      </a>
      {/* FAQ link (internal navigation) */}
      <a
        href="/faq"
        style={{ color: '#151618', fontSize: 16, textDecoration: 'none', marginBottom: 8, fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto' }}
        onClick={e => {
          e.preventDefault();
          onClose();
          navigate('/faq');
        }}
      >
        Frequently Asked Questions
      </a>
      {/* Success Stories anchor link */}
      {/* <a
        href="#success"
        style={{ color: '#151618', fontSize: 16, textDecoration: 'none', marginBottom: 8 }}
      >
        Success Stories
      </a> */}
      {/* Logout button only if logged in */}
      {/* LOGOUT button removed as per request */}
    </div>
  );
};

export default MobileHamburgerMenu;
